import {
  Activity,
  Bug,
  Clock,
  Disc,
  Heart,
  Home,
  Library,
  ListMusic,
  Plus,
  Radio,
  Search,
  Settings,
  Upload,
  X,
  Zap,
  Newspaper, // Step 1.1: Import Newspaper icon
} from "lucide-react";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useErrorStore } from "../stores/errorStore";
import { useProfileStore } from "../stores/profileStore";
import { useSidebarStore } from "../stores/sidebarStore";
import { UserProfile } from "./UserProfile";

interface SidebarProps {
  className?: string;
}

export const sidebarWidth = "18rem";
export const sidebarWidthInlineStyle = { "--sidebar-width": sidebarWidth } as React.CSSProperties;

export const Sidebar: React.FC<SidebarProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, closeSidebar } = useSidebarStore();
  const { errors } = useErrorStore();
  const { profile } = useProfileStore();

  useEffect(() => {
    closeSidebar();
  }, [location, closeSidebar]);

  const handleNavigation = (path: string) => {
    navigate(path);
    closeSidebar();
  };

  const NavItem = ({
    path,
    icon: Icon,
    label,
    errorCount,
  }: {
    path: string;
    icon: any;
    label: string;
    errorCount?: number;
  }) => {
    const isActive = location.pathname === path;
    return (
      <button
        onClick={() => handleNavigation(path)}
        className={`w-full flex items-center gap-3 py-2 px-4 transition-colors relative ${
          isActive ? "text-white bg-zinc-800 rounded-md" : "text-zinc-400 hover:text-white"
        }`}
      >
        <Icon className="w-5 h-5" />
        <span>{label}</span>
        {errorCount && errorCount > 0 && (
          <span className="absolute right-4 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
            {errorCount}
          </span>
        )}
      </button>
    );
  };

  const sidebarContent = (
    <>
      <div className="px-4 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/logo+white.png"
              width={48}
              height={48}
              className="w-12 h-12 text-yellow-500 -translate-y-0.5"
              alt="Haptics Logo"
              style={{
                aspectRatio: "1/1",
              }}
            />
            <div className="inline-flex flex-col text-lg uppercase font-bold text-white leading-[1.05rem]">
              Haptics
              <span className="text-[9px] uppercase font-extralight" style={{ letterSpacing: 1.65 }}>
                Music Player
              </span>
            </div>
          </div>
          {false && (
            <button onClick={closeSidebar} className="p-0 hover:bg-zinc-800 rounded-full transition md:hidden">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      <nav className="space-y-6 px-2 mb-12">
        <div className="space-y-1">
          <NavItem path="/" icon={Home} label="Home" />
          <NavItem path="/search" icon={Search} label="Search" />
          <NavItem path="/library" icon={Library} label="Library" />
          <NavItem path="/statistics" icon={Activity} label="Statistics" />
        </div>

        <div className="mt-8 space-y-1">
          <div className="flex justify-between px-4">
            <div className="p-0 text-sm font-medium text-zinc-400">Your Collection</div>
            <button
              onClick={() => handleNavigation("/playlists")}
              className="text-zinc-400 hover:text-white p-1 rounded-full hover:bg-zinc-800"
              aria-label="Create Playlist"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <NavItem path="/playlists" icon={ListMusic} label="Playlists" />
          <NavItem path="/albums" icon={Disc} label="Albums" />
          <NavItem path="/liked" icon={Heart} label="Liked Songs" />
          <NavItem path="/recent" icon={Clock} label="Recently Played" />
          <NavItem path="/local" icon={Upload} label="Local Files" />
        </div>

        <div className="mt-8 space-y-1">
          <div className="px-4 py-2 text-sm font-medium text-zinc-400">Discover</div>
          <NavItem path="/radio" icon={Radio} label="Radio" />
          <NavItem path="/blog" icon={Newspaper} label="Blog" /> {/* Step 1.2 & 1.3: Add NavItem for Blog */}
        </div>

        {profile.developerMode && (
          <div className="mt-8 space-y-1">
            <div className="px-4 py-2 text-sm font-medium text-zinc-400">Development</div>
            <NavItem path="/debug" icon={Bug} label="Debug Console" errorCount={errors.length} />
            <NavItem path="/test" icon={Zap} label="Test" />
          </div>
        )}
      </nav>

      <div className="mt-auto border-t border-zinc-800 pt-2.5 px-2">
        <NavItem path="/settings" icon={Settings} label="Settings" />
        <UserProfile />
      </div>
    </>
  );

  // Rest of the existing Sidebar component code remains unchanged
  return (
    <div className="bg-black m-0 md:m-[--outer-margin] overflow-hidden rounded-lg h-[calc(100svh-14px)] h-[calc(100vh-14px)]">
      {/* Desktop Sidebar */}
      <div
        style={sidebarWidthInlineStyle}
        className={`w-[--sidebar-width] bg-black h-screen flex-col text-gray-300 py-6 hidden md:flex ${className}`}
      >
        {sidebarContent}
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] md:hidden transition-opacity ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeSidebar}
      >
        <div
          style={sidebarWidthInlineStyle}
          className={`sidebar-inner w-[--sidebar-width] bg-black h-full !overflow-y-auto flex flex-col !pb-16 text-gray-300 py-6 transition-transform ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {sidebarContent}
        </div>
      </div>
    </div>
  );
};
